import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IntervalOption} from '../../../model/dataset/rendering/interval-option';
import {ColorUtils} from '../../../core/utils/color-utils';
import {GradientDetails} from '../../../model/legend/gradient-details';
import { MapThematicOverlayService } from '../../map-thematic-overlay.service';
import { Subscription } from 'rxjs';
import {ClimatologyOptions, ScenarioSSP, TimePeriod} from '../../../model/dataset/rendering/climatology-options';
import { SCENARIO_LABELS, TIME_PERIOD_LABELS, getScoreLabelForIndicator } from './legend-label.constants';
import { CONSOLIDATED_RISK_SCORE_LABELS } from '../../../shared/colorize-markers/colorize-markers.constants';

@Component({
    selector: 'map-datapoint-map-legend',
    templateUrl: './datapoint-map-legend.component.html',
    styleUrls: ['./datapoint-map-legend.component.scss']
})
export class DatapointMapLegendComponent implements OnInit, OnDestroy {

    @Input() intervalOptions: IntervalOption[] = [];
    @Input() datasetName: string;
    @Input() datasetId: string;
    @Input() fieldLabel: string;
    @Input() climatologyOptions: ClimatologyOptions;
    @Output() removeLegend = new EventEmitter<string>();

    _gradientDetails: GradientDetails;
    linearGradientValue: string;

    @Input() set gradientDetails(gradientDetails: GradientDetails) {
        if (gradientDetails) {
            // Create a copy to avoid mutating the input
            const reversedColors = [...gradientDetails.gradientColors].reverse();
            this._gradientDetails = {
                ...gradientDetails,
                gradientColors: reversedColors
            };
            this.linearGradientValue = `linear-gradient(to bottom, ${reversedColors.join(', ')})`;
        }
    }
    legendLimit = [5, 15, 50];
    private readonly subscriptions: Subscription = new Subscription();

    constructor(
        private readonly mapThematicOverlayService: MapThematicOverlayService) {}

    ngOnInit(): void {
        this.subscriptions.add(this.mapThematicOverlayService.onCustomizedLegends().subscribe((limit) => {
            if(limit.length > 0){
                this.legendLimit = limit;
            }
        }));
    }

    get ColorUtils() {
        return ColorUtils;
    }

    deleteLegend() {
        this.removeLegend.emit(this.datasetId);
    }
    
    /**
     * Get formatted scenario label (e.g., "SSP119" -> "SSP1-1.9")
     */
    getScenarioLabel(scenario: ScenarioSSP): string {
        return SCENARIO_LABELS[scenario] || scenario;
    }
    
    /**
     * Get formatted time period label (e.g., "near" -> "2021-2040")
     */
    getTimePeriodLabel(timePeriod: TimePeriod): string {
        return TIME_PERIOD_LABELS[timePeriod] || timePeriod;
    }
    
    /**
     * Get score text label for gradient legend (1-5 scale)
     * Based on Veolia's Acute/Chronic hazard probability/intensity scale
     * Uses helper function from legend-label.constants.ts
     *
     * The Consolidated Risk Score has its own labels (FR-1.49) and its own 0, which the
     * indicator scales have no equivalent of.
     */
    getScoreLabel(score: number): string {
        if (this.climatologyOptions?.vulnerabilityProfileId) {
            return CONSOLIDATED_RISK_SCORE_LABELS[score] || '';
        }
        return getScoreLabelForIndicator(score, this.climatologyOptions?.selectedIndicator);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
